import React from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Package, 
  Users, 
  BarChart3, 
  UsersRound, 
  ChevronRight, 
  ArrowRight,
  Target,
  Zap,
  ShieldCheck,
  Search,
  MessageSquare,
  Clock,
  Layout,
  Quote,
  Lock,
  RefreshCcw,
  CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePublic } from '../../context/PublicContext';
import SEO from '../../components/SEO';

const SectionWrapper = ({ children, className = "", bgColor = "bg-white", id = "" }) => (
  <section id={id} className={`py-10 px-6 md:px-10 lg:px-12 ${bgColor} ${className}`}>
    <div className="max-w-[1400px] mx-auto">
      {children}
    </div>
  </section>
);

const StaggeredCard = ({ number, subtitle, title, description, className = "" }) => (
  <div className={`relative p-8 space-y-6 group ${className}`}>
    <div className="absolute -top-10 -left-6 text-[12rem] font-black text-slate-100/50 select-none -z-10 transition-colors group-hover:text-emerald-100/50">
      {number}
    </div>
    
    <div className="space-y-4">
      <h5 className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em]">
        {subtitle}
      </h5>
      <h3 className="text-3xl font-bold text-slate-900 leading-tight">
        {title}
      </h3>
      <p className="text-slate-500 leading-relaxed font-medium">
        {description}
      </p>
    </div>

    <div className="pt-4">
      <Link to="/features" className="inline-flex items-center gap-3 text-slate-900 font-bold hover:text-emerald-600 transition-colors group/link">
        Learn more 
        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white group-hover/link:bg-emerald-600 transition-all">
          <ArrowRight size={16} />
        </div>
      </Link>
    </div>
  </div>
);

const DarkValueCard = ({ icon: Icon, title, description }) => (
  <div className="bg-white p-8 rounded-2xl border border-emerald-100 shadow-sm space-y-6 hover:shadow-md transition-all">
    <div className="text-emerald-600">
      <Icon size={32} strokeWidth={1.5} />
    </div>
    <div className="space-y-3">
      <h4 className="text-sm font-bold text-slate-900 uppercase tracking-[0.2em]">{title}</h4>
      <p className="text-slate-500 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

const AboutPage = () => {
  const { openDemoModal } = usePublic();
  return (
    <div className="min-h-screen bg-white font-lexend overflow-x-hidden">
      <SEO
        title="About Us - Gio Billing"
        description="Learn about our mission to build smarter, simpler billing tools for growing businesses."
        url="/about"
      />

      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap');`}
      </style>

      {/* 🔷 1. Hero Section (Bento Grid Style) */}
      <SectionWrapper bgColor="bg-white" className="pt-16 pb-12">
        <div className="space-y-12">
          {/* Centered Header */}
          <div className="text-center space-y-4 max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 tracking-tight leading-tight">
              Redefining the future of <br />
              <span className="text-emerald-500">retail management</span>
            </h1>
            <p className="text-slate-500 text-lg md:text-xl font-medium max-w-xl mx-auto leading-relaxed">
              Simplifying billing and operations for the next generation of businesses.
            </p>
          </div>

          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 h-auto md:h-[480px]">
            {/* Left: Tall Image (Explicit Transaction) */}
            <div className="md:col-span-4 h-full">
              <img 
                src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=800&auto=format&fit=crop" 
                alt="Modern Transaction" 
                className="w-full h-full object-cover rounded-[1.5rem] shadow-lg"
              />
            </div>

            {/* Middle Column */}
            <div className="md:col-span-4 flex flex-col gap-5 h-full">
              {/* Top: Stats Card (Orange) */}
              <div className="bg-[#ff5722] p-6 rounded-[1.5rem] text-white flex flex-col justify-center h-1/3">
                <span className="text-4xl font-bold mb-1">90%</span>
                <p className="text-sm font-bold opacity-90 tracking-wide uppercase">Faster Billing</p>
              </div>
              {/* Bottom: Transaction/Payment Visual */}
              <div className="h-2/3">
                <img 
                  src="https://images.unsplash.com/photo-1556740714-a8395b3bf30f?q=80&w=800&auto=format&fit=crop" 
                  alt="Digital Payment" 
                  className="w-full h-full object-cover rounded-[1.5rem] shadow-lg"
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="md:col-span-4 flex flex-col gap-5 h-full">
              {/* Top: Invoicing/Financial Auditing */}
              <div className="h-2/3">
                <img 
                  src="https://images.unsplash.com/photo-1554224154-26032ffc0d07?q=80&w=800&auto=format&fit=crop" 
                  alt="Financial Management" 
                  className="w-full h-full object-cover rounded-[1.5rem] shadow-lg"
                />
              </div>
              {/* Bottom: Stats Card (Black) */}
              <div className="bg-[#1a1a1a] p-6 rounded-[1.5rem] text-white flex flex-col justify-center h-1/3">
                <span className="text-4xl font-bold mb-1">50%</span>
                <p className="text-sm font-bold opacity-90 tracking-wide uppercase">Lower Costs</p>
              </div>
            </div>
          </div>
        </div>
      </SectionWrapper>

      {/* 🔷 2. Who We Are (Emerald 50) */}
      <SectionWrapper bgColor="bg-emerald-50/50">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          <div className="space-y-10">
            <h2 className="text-4xl font-black text-slate-900 tracking-tight uppercase">Who We Are</h2>
            <div className="space-y-6 text-slate-600 text-lg leading-relaxed font-medium">
              <p>
                Founded in 2022, Gio Billing is on a mission to simplify the way Indian businesses manage their 
                operations. We are an early-stage startup focused on stripping away the complexity of traditional 
                ERP systems to provide tools that are practical, reliable, and truly easy to use.
              </p>
              <p>
                Our team consists of passionate developers and business enthusiasts who believe that 
                every small business deserves the same technological advantages as giant corporations.
              </p>
            </div>
          </div>

          <div className="space-y-8 pl-0 md:pl-12">
            <div className="text-emerald-500 transform scale-x-[-1]">
              <Quote size={64} strokeWidth={1.5} />
            </div>
            <div className="w-24 h-1.5 bg-slate-900 rounded-full"></div>
            <div className="space-y-6">
              <h3 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
                "We believe that it takes great people to deliver a great product."
              </h3>
              <p className="text-4xl text-slate-900 tracking-tight" style={{ fontFamily: "'Dancing Script', cursive" }}>
                Gio Billing Team
              </p>
            </div>
          </div>
        </div>
      </SectionWrapper>

      {/* 🔷 3. Why We Started (White) */}
      <SectionWrapper bgColor="bg-white">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-slate-900 leading-tight uppercase tracking-tighter">Why We Started</h2>
            <div className="space-y-6 text-slate-600 text-xl font-medium leading-relaxed">
              <p>We saw thousands of small business owners in India struggling with manual ledgers and complex, overpriced software that they didn't need.</p>
              <p>Existing tools were either too expensive for growing shops or so complicated that they required a degree to operate.</p>
              <p>We started Gio Billing to bridge this gap—creating a simple, affordable, and powerful solution for the backbone of our economy.</p>
            </div>
            <div className="flex gap-4 pt-4">
              <div className="flex items-center gap-2 text-emerald-600 font-bold">
                <CheckCircle2 size={20} /> 100% Focused on SMEs
              </div>
              <div className="flex items-center gap-2 text-emerald-600 font-bold">
                <CheckCircle2 size={20} /> Built for Simplicity
              </div>
            </div>
          </div>
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="rounded-[2.5rem] overflow-hidden shadow-2xl"
          >
            <img 
              src="/assets/images/about/story.png" 
              alt="Why We Started" 
              className="w-full h-auto"
            />
          </motion.div>
        </div>
      </SectionWrapper>

      {/* 🔷 4. What We Do (Emerald 50) */}
      <SectionWrapper bgColor="bg-emerald-50/50">
        <div className="space-y-24">
          <div className="text-center space-y-4">
            <h4 className="text-emerald-600 font-bold uppercase tracking-[0.2em] text-sm">Capabilities</h4>
            <h2 className="text-5xl font-bold text-slate-900">What We Do</h2>
            <p className="text-slate-500 font-medium text-lg italic">
              No matter the size or focus of your business, we’ve got a solution for you.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 items-start pb-20">
            <StaggeredCard 
              number="01"
              subtitle="Invoice Management"
              title="GST Invoicing goes digital"
              description="Modern tax compliance is simplified. Generate professional, GST-ready invoices in seconds, even in high-speed retail environments."
              className="md:-translate-y-16"
            />
            <StaggeredCard 
              number="02"
              subtitle="Inventory & Staff"
              title="We speak your language"
              description="Managing multiple outlets and stock shouldn't be complex. We provide a platform that understands the nuances of multi-store operations."
              className="md:translate-y-16"
            />
            <StaggeredCard 
              number="03"
              subtitle="Analytics & Growth"
              title="Tracks for Business Growth"
              description="Real-time sales insights and automated reports keep you ahead of the curve. Monitor every transaction from anywhere in the world."
              className="md:translate-y-32"
            />
          </div>
        </div>
      </SectionWrapper>

      {/* 🔷 6. Our Approach (White with Emerald Cards) */}
      <section className="py-12 px-6 md:px-10 lg:px-12 bg-white">
        <div className="max-w-[1400px] mx-auto space-y-16">
          <div className="text-center space-y-4">
            <h4 className="text-emerald-500 font-bold uppercase tracking-[0.3em] text-sm">Principles</h4>
            <h2 className="text-5xl md:text-6xl font-bold text-slate-900 tracking-tight">Our Approach</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <DarkValueCard 
              icon={Zap}
              title="Simplicity First"
              description="We strip away the noise to focus on what actually matters for your business."
            />
            <DarkValueCard 
              icon={Target}
              title="Practical Tools"
              description="Every feature is built to solve a real-world challenge you face every day."
            />
            <DarkValueCard 
              icon={ShieldCheck}
              title="Reliable Tech"
              description="Stability you can count on for every single transaction, no matter the volume."
            />
            <DarkValueCard 
              icon={UsersRound}
              title="User-Centric"
              description="We evolve every single day based on the direct feedback from our business community."
            />
            <DarkValueCard 
              icon={Lock}
              title="Data Secure"
              description="Bank-grade encryption and 24/7 backups to ensure your peace of mind."
            />
            <DarkValueCard 
              icon={RefreshCcw}
              title="Always Evolving"
              description="Constant innovation to keep your business ahead of the changing market landscape."
            />
          </div>
        </div>
      </section>

      {/* 🔷 9. Final CTA (Emerald Card Style) */}
      <section className="py-12 px-6 md:px-10 lg:px-12 bg-white">
        <div className="max-w-[1400px] mx-auto bg-emerald-600 rounded-[2.5rem] p-10 md:p-16 relative overflow-hidden shadow-2xl">
          {/* Subtle Abstract Shapes */}
          <div className="absolute right-0 top-0 w-1/3 h-full opacity-10 pointer-events-none">
            <div className="absolute top-1/2 -right-20 w-64 h-64 rounded-full border-[40px] border-white" />
            <div className="absolute top-1/4 -right-10 w-48 h-48 rounded-full border-[20px] border-white" />
          </div>

          <div className="relative z-10 space-y-8 max-w-2xl">
            <h2 className="text-4xl md:text-6xl font-bold text-white leading-tight tracking-tight">
              Take control of your <br /> business today
            </h2>
            <p className="text-emerald-50 text-lg md:text-xl font-medium">
              Join over 1,000+ businesses and partners already growing with Gio Billing.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <button 
                onClick={openDemoModal}
                className="bg-white text-emerald-600 px-10 py-4 rounded-2xl font-bold text-lg hover:bg-emerald-50 transition-all shadow-lg active:scale-95"
              >
                Get started
              </button>
              <Link 
                to="/contact" 
                className="bg-emerald-500/50 text-white border border-emerald-400/50 px-10 py-4 rounded-2xl font-bold text-lg hover:bg-emerald-500/70 transition-all active:scale-95 backdrop-blur-sm"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
